import { useEffect, useState } from 'react';
import { collection, onSnapshot, query, where, Timestamp, getCountFromServer, getDocs, deleteDoc } from 'firebase/firestore';
import { AreaChart, Area, ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid } from 'recharts';
import { db } from '../../firebase';
import { useTheme } from '../../context/ThemeContext';

const StatCard = ({ title, value, icon, color }) => {
  return (
    <div className={`p-5 rounded-xl shadow-sm border ${color?.border} ${color?.bg}`}>
      <div className="flex items-center">
        <div className={`p-3 rounded-lg mr-4 ${color?.iconBg}`}>
          <span className="text-xl">{icon}</span>
        </div>
        <div>
          <h3 className={`text-sm font-medium ${color?.title}`}>{title}</h3>
          <p className={`text-2xl font-bold ${color?.value}`}>{value}</p>
        </div>
      </div>
      <div className={`mt-3 pt-3 ${color?.divider} border-t`}>
        <p className={`text-xs ${color?.subtitle}`}>Atualizado em tempo real</p>
      </div>
    </div>
  );
};

const AdminAnalyticsPanel = () => {
  const { isDarkMode } = useTheme();
  const [loading, setLoading] = useState(true);
  const [visits, setVisits] = useState(0); // Acessos (Sessões únicas)
  const [impressions, setImpressions] = useState(0); // Impressões (Page loads)
  const [activeUsers, setActiveUsers] = useState(0);
  const [series, setSeries] = useState([]);
  const [cleanupLoading, setCleanupLoading] = useState(false);
  const [cleanupResult, setCleanupResult] = useState('');

  useEffect(() => {
    const unsubscribeList = [];
    let verifyInterval = null;

    const startOfDay = new Date();
    startOfDay.setHours(0, 0, 0, 0);
    const startTs = Timestamp.fromDate(startOfDay);

    const dailyMetricsQuery = query(
      collection(db, 'dailyMetrics'),
      where('date', '>=', startTs)
    );

    const unsubDaily = onSnapshot(dailyMetricsQuery, (snapshot) => {
      let v = 0; // Visits
      let imp = 0; // Impressions
      snapshot.forEach((doc) => {
        const data = doc.data();
        const type = (data.metricType || '').toLowerCase();
        const val = Number(data.value || 0);
        
        // Contabiliza Acessos (Visits)
        if (type === 'visits' || type === 'acessos' || type === 'visitas') v += val;
        
        // Contabiliza Impressões
        if (type === 'impressions' || type === 'impressões' || type === 'pageviews') imp += val;
      });
      setVisits(v);
      setImpressions(imp);
      setLoading(false);
    });
    unsubscribeList.push(unsubDaily);

    // Removido: não usamos appMetrics para activeUsers em tempo real

    const lastHour = new Date(Date.now() - 60 * 60 * 1000);
    const lastHourTs = Timestamp.fromDate(lastHour);
    const realtimePvQuery = query(
      collection(db, 'realtimeMetrics'),
      where('metricType', 'in', ['pageview', 'visit']),
      where('timestamp', '>=', lastHourTs)
    );
    const unsubRealtime = onSnapshot(realtimePvQuery, (snapshot) => {
      const buckets = new Map();
      snapshot.forEach((doc) => {
        const data = doc.data();
        const type = data.metricType;
        const ts = data.timestamp?.toDate ? data.timestamp.toDate() : new Date(data.timestamp);
        const minuteKey = new Date(ts);
        minuteKey.setSeconds(0, 0);
        const key = minuteKey.toISOString();
        
        if (!buckets.has(key)) buckets.set(key, { visits: 0, impressions: 0 });
        const entry = buckets.get(key);
        
        if (type === 'visit') entry.visits += 1;
        if (type === 'pageview') entry.impressions += 1;
      });
      
      const points = [];
      for (let i = 59; i >= 0; i--) {
        const t = new Date();
        t.setMinutes(t.getMinutes() - i, 0, 0);
        const key = t.toISOString();
        const data = buckets.get(key) || { visits: 0, impressions: 0 };
        points.push({ 
          time: `${t.getHours()}:${String(t.getMinutes()).padStart(2, '0')}`, 
          visits: data.visits,
          impressions: data.impressions
        });
      }
      setSeries(points);
      setLoading(false);
    });
    unsubscribeList.push(unsubRealtime);

    // Active Users: Polling robusto com janela deslizante
    const fetchActiveUsers = async () => {
      try {
        // Considera ativo quem interagiu nos últimos 60 segundos
        const threshold = new Date(Date.now() - 60 * 1000);
        const q = query(
          collection(db, 'portalPresence'),
          where('lastActiveAt', '>=', Timestamp.fromDate(threshold))
        );
        const snapshot = await getCountFromServer(q);
        setActiveUsers(snapshot.data().count);
      } catch (err) {
        console.error("Erro ao buscar usuários ativos:", err);
      }
    };

    fetchActiveUsers();
    verifyInterval = setInterval(fetchActiveUsers, 10000);

    return () => {
      unsubscribeList.forEach((fn) => fn && fn());
      if (verifyInterval) clearInterval(verifyInterval);
    };
  }, []);

  const colors = {
    light: {
      bg: 'bg-white',
      border: 'border-gray-100',
      iconBg: 'bg-blue-50',
      title: 'text-gray-500',
      value: 'text-gray-800',
      divider: 'border-gray-100',
      subtitle: 'text-gray-500',
    },
    dark: {
      bg: 'bg-gray-800',
      border: 'border-gray-700',
      iconBg: 'bg-blue-900/50',
      title: 'text-gray-400',
      value: 'text-white',
      divider: 'border-gray-700',
      subtitle: 'text-gray-500',
    }
  };

  const base = isDarkMode ? colors.dark : colors.light;

  return (
    <div className="space-y-6">
      <div className={`p-5 rounded-xl shadow-sm border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-100'}`}>
        <div className="flex items-center justify-between">
          <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Analytics em Tempo Real</h2>
          <span className={`text-xs px-2 py-1 rounded ${isDarkMode ? 'bg-green-900/40 text-green-300' : 'bg-green-100 text-green-700'}`}>Live</span>
        </div>
        <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'} mt-2`}>Acompanhe acessos, impressões e usuários ativos do site.</p>
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-40">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <span className={`${isDarkMode ? 'text-gray-300' : 'text-gray-600'} ml-3`}>Carregando métricas...</span>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <StatCard
            title="Acessos"
            value={visits}
            icon="👀"
            color={{
              bg: base.bg,
              border: base.border,
              iconBg: isDarkMode ? 'bg-indigo-900/50' : 'bg-indigo-50',
              title: base.title,
              value: base.value,
              divider: base.divider,
              subtitle: base.subtitle,
            }}
          />
          <StatCard
            title="Impressões"
            value={impressions}
            icon="📣"
            color={{
              bg: base.bg,
              border: base.border,
              iconBg: isDarkMode ? 'bg-amber-900/50' : 'bg-amber-50',
              title: base.title,
              value: base.value,
              divider: base.divider,
              subtitle: base.subtitle,
            }}
          />
          <StatCard
            title="Usuários Ativos"
            value={activeUsers}
            icon="👥"
            color={{
              bg: base.bg,
              border: base.border,
              iconBg: isDarkMode ? 'bg-emerald-900/50' : 'bg-emerald-50',
              title: base.title,
              value: base.value,
              divider: base.divider,
              subtitle: base.subtitle,
            }}
          />
        </div>
      )}

      <div className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-100'} p-5 rounded-xl shadow-sm border`}>
        <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'} mb-3`}>Acessos e Impressões (últimos 60 min)</h3>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={series} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="colorVisits" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.6}/>
                  <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                </linearGradient>
                <linearGradient id="colorImpressions" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.6}/>
                  <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? '#374151' : '#e5e7eb'} />
              <XAxis dataKey="time" tick={{ fill: isDarkMode ? '#9ca3af' : '#6b7280' }} />
              <YAxis tick={{ fill: isDarkMode ? '#9ca3af' : '#6b7280' }} allowDecimals={false} />
              <Tooltip contentStyle={{ backgroundColor: isDarkMode ? '#1f2937' : '#ffffff', borderColor: isDarkMode ? '#374151' : '#e5e7eb', color: isDarkMode ? '#e5e7eb' : '#111827' }} />
              <Area type="monotone" dataKey="impressions" name="Impressões" stroke="#8b5cf6" fillOpacity={1} fill="url(#colorImpressions)" />
              <Area type="monotone" dataKey="visits" name="Acessos" stroke="#3b82f6" fillOpacity={1} fill="url(#colorVisits)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-100'} p-5 rounded-xl shadow-sm border`}>
        <div className="flex items-center justify-between">
          <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Limpeza de coleções antigas</h3>
          <button
            onClick={async () => {
              if (cleanupLoading) return;
              setCleanupLoading(true);
              setCleanupResult('');
              try {
                const presSnap = await getDocs(collection(db, 'presenceSessions'));
                const presCount = presSnap.size;
                await Promise.all(presSnap.docs.map(d => deleteDoc(d.ref)));
                const dmSnap = await getDocs(query(collection(db, 'dailyMetrics'), where('metricType', 'in', ['activeusers','active_users','usuariosativos'])));
                const dmCount = dmSnap.size;
                await Promise.all(dmSnap.docs.map(d => deleteDoc(d.ref)));
                setCleanupResult(`Removidos: presenceSessions=${presCount}, dailyMetrics(activeUsers)=${dmCount}`);
              } catch (e) {
                setCleanupResult('Erro na limpeza.');
              } finally {
                setCleanupLoading(false);
              }
            }}
            className={`px-4 py-2 rounded-lg font-semibold ${cleanupLoading ? 'bg-gray-500 text-white cursor-not-allowed' : 'bg-red-600 text-white hover:bg-red-700'}`}
            type="button"
          >
            {cleanupLoading ? 'Limpando...' : 'Excluir coleções antigas'}
          </button>
        </div>
        {cleanupResult && (
          <p className={`${isDarkMode ? 'text-gray-300' : 'text-gray-700'} mt-3 text-sm`}>{cleanupResult}</p>
        )}
      </div>
    </div>
  );
};

export default AdminAnalyticsPanel;